import React, { FC } from 'react';
import { Route, useParams } from 'react-router';
import { Grid } from '@material-ui/core';
import { Chat } from 'stream-chat-react';

import { GetContentDocument, useGetContentQuery, useOnMediaStatusSubscription } from 'api';
import { PostAction } from 'types/app';
import { ContentProvider, DimensionsProvider, QueryParamsProvider } from 'providers';
import { contentModel } from 'apollo/reducers';
import { useChatClient, useCurrentUser, useFeatures } from 'hooks';
import { Dimensions, Layouts, ErrorPage, QueryManager } from 'components';
import { Post, IPostRenderProps } from 'features/posting';
import { InlineChannel } from 'features/discussions';

import { getLayout } from '../helpers';

const Permalink: FC = () => {
  const { content_id } = useParams();
  const { comments } = useFeatures();
  const currentUser = useCurrentUser();
  const chatClient = useChatClient();

  const getContentResult = useGetContentQuery({
    variables: { content_id },
  });

  const queryParams = {
    query: GetContentDocument,
    variables: { content_id },
  };

  useOnMediaStatusSubscription({
    variables: { user_id: currentUser.id },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const { onMediaStatus } = subscriptionData.data;

      contentModel({
        contentId: onMediaStatus.content_id,
        cache: client.cache,
        result: subscriptionData,
        ...queryParams,
      });
    },
  });

  function renderContent() {
    const { data } = getContentResult;
    const { getContent } = data;

    const ContentComponent = getLayout(getContent);

    return (
      <QueryParamsProvider value={queryParams}>
        <ContentProvider value={{ content: getContent }}>
          <Dimensions>
            {dimensions => (
              <DimensionsProvider value={{ dimensions }}>
                <Post action={PostAction.Edit} key={getContent.content_id}>
                  {(renderProps: IPostRenderProps) => <ContentComponent {...renderProps} />}
                </Post>
              </DimensionsProvider>
            )}
          </Dimensions>
        </ContentProvider>
      </QueryParamsProvider>
    );
  }

  function renderLegacy() {
    return (
      <QueryManager {...getContentResult}>
        {({ data }) => {
          if (!data || !data.getContent) {
            return <Route component={() => <ErrorPage type="not-found" />} />;
          }
          return renderContent();
        }}
      </QueryManager>
    );
  }

  function renderDiscussionsLayout() {
    return (
      <QueryManager {...getContentResult}>
        {({ data }) => {
          if (!data || !data.getContent) {
            return <Route component={ErrorPage} />;
          }

          return (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderContent()}
              </Grid>
              <Grid item xs={12} md={6}>
                {chatClient && (
                  <Chat client={chatClient}>
                    <InlineChannel id={data.getContent.content_id} />
                  </Chat>
                )}
              </Grid>
            </Grid>
          );
        }}
      </QueryManager>
    );
  }

  if (!comments) {
    return (
      <Layouts.App1Column
        navigation
        desktopSize={6}
        main={<Layouts.AppMain1Column main={renderLegacy()} />}
      />
    );
  }

  return (
    <Layouts.App1Column
      navigation
      desktopSize={12}
      main={<Layouts.AppMain1Column main={renderDiscussionsLayout()} />}
    />
  );
};

export default Permalink;
